"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

import Footer from "../../components/layout/footer";
import Header from "../../components/layout/header";
import PartnershipSlider from "../../components/partnership";

import COOPERATION from "../../assets/partners/corporate.jpeg";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

const COLORS = {
  primary: "#003B5C",
  teal: "#00778B",
  gray: "#C7C9C7",
  gold: "#E3A130",
};

const tw = {
  primaryBg: "bg-[#003B5C]",
  primaryText: "text-[#003B5C]",
  tealBg: "bg-[#00778B]",
  tealText: "text-[#00778B]",
  goldBg: "bg-[#E3A130]",
  goldText: "text-[#E3A130]",
  grayText: "text-[#C7C9C7]",
};

export default function HomePage() {
  const [darkMode, setDarkMode] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const saved =
      typeof window !== "undefined" ? localStorage.getItem("darkMode") : null;
    const prefers =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initial = saved !== null ? saved === "true" : prefers;
    setDarkMode(initial);
    if (typeof document !== "undefined")
      document.documentElement.classList.toggle("dark", initial);
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (darkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    localStorage.setItem("darkMode", String(darkMode));
  }, [darkMode, mounted]);

  // ---------- Form state ----------
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });
  const [fileName, setFileName] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");

  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setFileName("");
      setFileObj(null);
      return;
    }

    // Validasi tipe file sederhana
    if (file.type !== "application/pdf") {
      setStatusType("error");
      setStatusMessage("File must be a PDF.");
      setFileName("");
      setFileObj(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Validasi ukuran max 2MB
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setStatusType("error");
      setStatusMessage("PDF size exceeds 2MB limit.");
      setFileName("");
      setFileObj(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setStatusMessage("");
    setStatusType("");
    setFileName(file.name);
    setFileObj(file);
  };

  const scrollToId = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage("");
    setStatusType("");

    // Basic validation
    if (!form.name || !form.email || !form.phone) {
      setStatusType("error");
      setStatusMessage("Name, Email, and Phone are required.");
      return;
    }

    if (!fileObj) {
      setStatusType("error");
      setStatusMessage("Please upload a proposal in PDF format (max 2MB).");
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("address", form.address || "");
      formData.append("pdf", fileObj);

      const res = await fetch(`${API_BASE}/partnerships`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        let detail = "Failed to submit partnership proposal.";
        try {
          const data = await res.json();
          if (data?.detail) detail = data.detail;
        } catch (_) {
          // ignore JSON error
        }
        throw new Error(detail);
      }

      // Optional: baca response kalau mau dipakai
      // const data = await res.json();

      setStatusType("success");
      setStatusMessage(
        "Your partnership proposal has been submitted successfully."
      );

      // Reset form
      setForm({
        name: "",
        email: "",
        phone: "",
        address: "",
      });
      setFileName("");
      setFileObj(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      setStatusType("error");
      setStatusMessage(err.message || "Failed to submit partnership proposal.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header />

      {/* ================= HERO ================= */}
      <section className="relative" id="hero">
        <div className="relative h-[100vh] sm:h-[80vh] w-full overflow-hidden">
          {/* Background foto cooperation */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${COOPERATION.src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          {/* Overlay shadow primary (supaya teks kebaca) */}
          <div className="absolute inset-0 bg-[#003B5C]/70" />

          {/* Overlay + text center */}
          <div className="relative z-10 h-full w-full flex items-center justify-center">
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight drop-shadow text-center">
                Open Partnership
              </h1>

              <p className="mt-4 text-sm sm:text-base md:text-lg text-zinc-100">
                Register yourself and your business to benefit of the UIII
                Business Development Partnership.
              </p>
              <button
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-[#E3A130] text-black hover:opacity-90"
                onClick={() => scrollToId("join-partnership")}
              >
                Join Partnership <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= TEXT CONTENT ================= */}
      <section className="bg-white py-10 sm:py-12 border-b border-zinc-100">
        <div className="max-w-5xl mx-auto px-4 lg:px-0 space-y-5 text-sm sm:text-base text-zinc-700 leading-relaxed">
          <p>
            At UIII, we believe that meaningful progress is built through strong
            and inclusive partnerships. Our Collaboration Unit welcomes
            cooperation with institutions, businesses, government agencies, and
            community organizations across diverse sectors. We offer flexible
            partnership opportunities that support academic advancement,
            research development, commercial initiatives, and community
            engagement. With a commitment to professionalism, transparency, and
            mutual benefit, we are ready to build synergistic collaborations
            that create lasting impact. Whether you seek joint programs, service
            partnerships, resource sharing, or strategic initiatives, UIII
            provides an open and supportive environment to explore and grow
            together.
          </p>
        </div>
      </section>

      {/* ================= JOIN A PARTNERSHIP ================= */}
      <section
        id="join-partnership"
        className="bg-[#F3F4F6] py-10 sm:py-14 border-b border-zinc-200"
      >
        <div className="max-w-5xl mx-auto px-4 lg:px-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-[#003B5C] mb-6">
            Join a partnership
          </h2>

          <form
            onSubmit={handleSubmit}
            className="bg-[#E5E7EB] rounded-xl p-6 sm:p-8 space-y-4 shadow-sm"
          >
            {/* Name */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-[#003B5C] mb-1">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#003B5C]"
              />
            </div>
            {/* Email */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-[#003B5C] mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#003B5C]"
              />
            </div>
            {/* Phone */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-[#003B5C] mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#003B5C]"
              />
            </div>
            {/* Address */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-[#003B5C] mb-1">
                Address
              </label>
              <textarea
                name="address"
                rows={3}
                value={form.address}
                onChange={handleChange}
                className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#003B5C]"
              />
            </div>

            {/* Status message */}
            {statusMessage && (
              <div
                className={`text-xs sm:text-sm rounded-md px-3 py-2 ${
                  statusType === "success"
                    ? "bg-green-100 text-green-700"
                    : statusType === "error"
                    ? "bg-red-100 text-red-700"
                    : "bg-zinc-100 text-zinc-700"
                }`}
              >
                {statusMessage}
              </div>
            )}

            {/* Upload + Submit row */}
            <div className="mt-4 bg-[#D1D5DB] rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 text-xs sm:text-sm text-[#003B5C]">
                Submit your proposal to become part of the{" "}
                <span className="font-semibold">
                  UIII Business Development Partnership
                </span>
              </div>

              <div className="flex-1 flex flex-col sm:flex-row gap-2 items-stretch">
                {/* File upload */}
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full inline-flex items-center justify-between gap-2 rounded-full bg:white px-4 py-2 text-xs sm:text-sm text-[#003B5C] border border-zinc-300 hover:bg-zinc-50 bg-white"
                  >
                    <span className="truncate">
                      {fileName || "PDF format file (max 2MB)"}
                    </span>
                    <span className="text-[10px] sm:text-xs text-zinc-500">
                      Browse
                    </span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 rounded-full bg-[#003B5C] text-white text-xs sm:text-sm font-semibold hover:bg-[#00263c] w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* ================= PARTNERS (slider) ================= */}
      <section className="bg-white py-10 sm:py-12">
        <div className="max-w-5xl mx-auto px-4 lg:px-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-[#003B5C] mb-6">
            Partners
          </h2>

          <PartnershipSlider />
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <Footer />
    </div>
  );
}
